import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'

import { useAppDispatch } from '#/app/store/store'
import { Message } from '#/entities/message'
import { setCurrentChat, setPendingFocus } from '#/features/chats'
import { chatMessagesApi } from '#/widgets/chat-bot/api/chat-messages-api'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'

import { normalizeSlug, slugFromModel, slugFromSearchHit } from '../model/model-slug'
import { isSearchableQuery, splitHighlight } from '../model/query'
import { pushRecentChat, readRecentChats, writePendingChatTarget } from '../model/storage'
import { ChatSearchHit, RecentChat } from '../model/types'
import { useChatSearch } from '../model/use-chat-search'

import styles from './chat-search.module.scss'

type ChatSearchProps = {
	variant: 'desktop' | 'mobile'
}

function getSearchShortcutLabel() {
	if (typeof navigator === 'undefined') {
		return 'Alt+F'
	}
	const userAgentData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
	const platform = userAgentData?.platform || navigator.platform || navigator.userAgent
	return /mac|iphone|ipad|ipod/i.test(platform) ? '⌥+F' : 'Alt+F'
}

function Highlighted({ text, query }: { text: string; query: string }) {
	return (
		<>
			{splitHighlight(text, query).map((part, index) =>
				part.match ? (
					<mark key={index} className={styles.mark}>
						{part.text}
					</mark>
				) : (
					<span key={index}>{part.text}</span>
				)
			)}
		</>
	)
}

function hitTitle(hit: ChatSearchHit) {
	const modelTitle = typeof hit.model === 'string' ? hit.model : hit.model?.title
	return hit.model_name?.trim() || modelTitle?.trim() || hit.model_slug || 'Модель'
}

export function ChatSearch({ variant }: ChatSearchProps) {
	const router = useRouter()
	const dispatch = useAppDispatch()
	const { data } = useSession()
	const { showMessage } = useShowDataStore()
	const inputRef = useRef<HTMLInputElement>(null)
	const [focused, setFocused] = useState(false)
	const [mobileOpen, setMobileOpen] = useState(false)
	const [opening, setOpening] = useState(false)
	const [recent, setRecent] = useState<RecentChat[]>([])
	const search = useChatSearch(data?.access)

	const showDropdown = variant === 'desktop' ? focused : mobileOpen

	useEffect(() => {
		if (variant !== 'desktop') {
			return
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.altKey && !event.ctrlKey && !event.metaKey && event.code === 'KeyF') {
				event.preventDefault()
				refreshRecent()
				setFocused(true)
				inputRef.current?.focus()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [variant])

	const refreshRecent = () => {
		setRecent(readRecentChats())
	}

	const dismissSearch = () => {
		setFocused(false)
		setMobileOpen(false)
		inputRef.current?.blur()
	}

	const openChat = async (target: { chatUid: string; modelSlug?: string | null; messageUid?: string; title?: string }) => {
		if (opening) {
			return
		}
		setOpening(true)
		try {
			let modelSlug = normalizeSlug(target.modelSlug)
			if (!modelSlug) {
				const messages = await chatMessagesApi.getMessages(target.chatUid, 0, data?.access)
				if (!Array.isArray(messages) || messages.length === 0) {
					showMessage('Не удалось открыть чат')
					return
				}
				const withModel = messages.find((message: Message) => slugFromModel(message.model))
				modelSlug = slugFromModel(withModel?.model ?? messages[0]?.model)
			}
			if (!modelSlug) {
				showMessage('Не удалось открыть чат')
				return
			}
			if (target.title) {
				pushRecentChat({ chatUid: target.chatUid, title: target.title, modelSlug })
			}
			writePendingChatTarget({
				chatUid: target.chatUid,
				modelSlug,
				messageUid: target.messageUid,
			})
			if (target.messageUid) {
				dispatch(
					setPendingFocus({
						chatUid: target.chatUid,
						messageUid: target.messageUid,
						modelSlug,
						requestId: Date.now(),
					})
				)
			}
			dispatch(setCurrentChat(target.chatUid))
			search.setQuery('')
			dismissSearch()
			if (router.asPath.split('?')[0] === `/chat-bot/${modelSlug}`) {
				return
			}
			await router.push(`/chat-bot/${modelSlug}`)
		} finally {
			setOpening(false)
		}
	}

	const openHit = (hit: ChatSearchHit) => {
		void openChat({
			chatUid: hit.chat_uid,
			modelSlug: slugFromSearchHit(hit),
			messageUid: hit.message_uid,
			title: hit.title,
		})
	}

	const openRecent = (item: RecentChat) => {
		void openChat({
			chatUid: item.chatUid,
			modelSlug: normalizeSlug(item.modelSlug),
			title: item.title,
		})
	}

	const showHint = search.query.trim().length > 0 && !isSearchableQuery(search.query)
	const showRecent = search.query.trim().length === 0
	const panel = (
		<div
			className={variant === 'mobile' ? styles.overlayList : styles.dropdown}
			onMouseDown={(event) => {
				// Keep input focused while interacting with the dropdown.
				event.preventDefault()
			}}
		>
			{showRecent ? (
				<>
					<div className={styles.section}>Недавние чаты</div>
					{recent.length === 0 ? (
						<div className={styles.status}>Пока нет недавних чатов</div>
					) : (
						recent.map((item) => (
							<button key={item.chatUid} type='button' className={styles.item} onClick={() => openRecent(item)}>
								<Image src='/svg/side-menu/chat.svg' width={20} height={20} alt='' />
								<div className={styles.title}>{item.title}</div>
							</button>
						))
					)}
				</>
			) : null}
			{showHint ? <div className={styles.status}>Введите от 3 символов в каждом слове, не больше 8 слов</div> : null}
			{search.loading ? <div className={styles.status}>Ищем…</div> : null}
			{search.unavailable ? <div className={styles.status}>Поиск временно недоступен. Повторите запрос</div> : null}
			{search.searched && !search.loading && !search.unavailable && search.results.length === 0 ? (
				<div className={styles.status}>По вашему запросу ничего не найдено</div>
			) : null}
			{search.results.map((hit) => (
				<button
					key={`${hit.chat_uid}-${hit.message_uid}-${hit.start}`}
					type='button'
					className={styles.item}
					onClick={() => openHit(hit)}
				>
					<Image src='/svg/side-menu/chat.svg' width={20} height={20} alt='' />
					<div>
						<div className={styles.title}>
							<Highlighted text={hitTitle(hit)} query={search.debouncedQuery} />
						</div>
						<div className={styles.snippet}>
							<Highlighted text={hit.slice} query={search.debouncedQuery} />
						</div>
					</div>
				</button>
			))}
		</div>
	)

	const input = (
		<div className={styles.field}>
			<Image className={styles.icon} src='/search.svg' width={16} height={16} alt='' />
			<input
				ref={inputRef}
				className={styles.input}
				value={search.query}
				placeholder='Поиск по платформе'
				onChange={(event) => search.setQuery(event.target.value)}
				onFocus={() => {
					refreshRecent()
					setFocused(true)
				}}
				onClick={() => {
					refreshRecent()
					setFocused(true)
				}}
				onBlur={() => {
					if (variant === 'desktop') {
						setFocused(false)
					}
				}}
			/>
			{variant === 'desktop' ? <span className={styles.shortcut}>{getSearchShortcutLabel()}</span> : null}
			{variant === 'desktop' && showDropdown ? panel : null}
		</div>
	)

	if (variant === 'mobile') {
		return (
			<>
				<button
					type='button'
					className={styles.mobileButton}
					aria-label='Поиск по платформе'
					onClick={() => {
						refreshRecent()
						setMobileOpen(true)
					}}
				>
					<Image src='/search.svg' width={22} height={22} alt='' />
				</button>
				{mobileOpen ? (
					<div className={styles.overlay}>
						<div className={styles.overlayBar}>
							<button type='button' className={styles.back} aria-label='Назад' onClick={dismissSearch}>
								‹
							</button>
							{input}
						</div>
						{panel}
					</div>
				) : null}
			</>
		)
	}

	return input
}
